// @ts-nocheck
import _ from 'lodash';
import { fetchGridData } from 'models/dynamicGrid/sagas';
import { createComponent } from 'models/tableColumns/gridComponents';
import { getColumnInfoByIndentifier, getColumnInfoByQuery } from 'models/tableColumns/selector';
import { getDispatch, getState } from 'reducers';
import { getGridData, getGridFilters, getGridMetadata, getTableSelection } from 'models/dynamicGrid/selector';
import * as dynamicGridSagaActions from '../../../models/dynamicGrid/sagaActions';
import { Bookmarks } from './bookmarks/bookmarks';
import { PAGE_SIZE } from './constants';
import GridActionHandler from './gridActions';

export const buildTree = (data, keys, parentNode, currentKeyIndex = 0, aggregationColumns, tableId) => {
	const currentKey = keys[currentKeyIndex];
	for (const value of data[currentKey]) {
		const node = {
			groupId: parentNode.groupId ? `${parentNode.groupId}|${value}` : value,
			headerName: value,
			children: [],
		};
		if (currentKeyIndex === keys.length - 1) {
			// If this is the last key, create the aggregation column nodes
			for (const column of aggregationColumns) {
				let col = getColumnInfoByIndentifier(getState(), tableId, column);
				const columnNode = {
					colId: `${node.groupId}|${column}`,
					headerName: `${col.AppColumnAliasName || col.AppColumn}`,
					metaData: { ...col, tableId },
					cellRenderer: (props) => {
						return createComponent(col.ComponentName, {
							...props,

							rowIdentifier: col.AppColumnName,
							tableId,
							type: col.ColumnType,
							// defaultRowGroupColumn,
							isPivotViewEnabled: true,
							suppressMovable: true,
						});
					},
					field: `${node.groupId}|${column}`,
					resizable: true,
				};
				node.children.push(columnNode);
			}
		} else {
			// If this is not the last key, recursively build the tree
			buildTree(data, keys, node, currentKeyIndex + 1, aggregationColumns, tableId);
		}
		parentNode.children.push(node);
	}
};

export function createServerSideDatasource(server, gridRef, options, setColumns) {
	let colDefHashExt;

	const createColsHash = (colDefs) => {
		if (!colDefs) {
			return null;
		}
		var parts = [];
		colDefs.forEach(function (colDef) {
			if (colDef.children) {
				parts.push(colDef.groupId);
				parts.push('[' + createColsHash(colDef.children) + ']');
			} else {
				parts.push(colDef.colId);
				// headerName can change if the aggFunc was changed in a value col. if we didn't
				// do this, then the grid would not pick up on new header names as we move from
				// eg min to max.
				if (colDef.headerName) {
					parts.push(colDef.headerName);
				}
			}
		});
		return parts.join(',');
	};
	const setSecondaryColsIntoGrid = (secondaryColDefs) => {
		const colDefHash = createColsHash(secondaryColDefs);
		if (colDefHashExt !== colDefHash) {
			gridRef?.current?.api?.setPivotResultColumns(secondaryColDefs); // updated line
			colDefHashExt = colDefHash;
		}
	};
	let totalRecordCount = 0;
	return {
		getRows: (params) => {
			const tableId = gridRef?.current?.props?.defaultColDef?.context?.tableId;
			if (tableId === 115 && params?.request?.groupKeys.length) {
				const state = getState();
				const metaData = getGridData(state, tableId) ?? [];
				const filteredValues = params?.request?.groupKeys;
				// Filter data based on non-null, non-undefined values
				const cacheData = metaData.filter((item) =>
					filteredValues.every((filterValue) =>
						Object.values(item).some(
							(value) => value != null && String(value).trim() === filterValue.trim()
						)
					)
				);
				params.success({
					rowData: cacheData,
					rowCount: 1,
				});
				return;
			}
			const response = server.getData(params.request, options, gridRef);
			response
				.then((response = {}) => {
					const pageNumber = params.request.endRow ? Math.ceil(params.request.endRow / PAGE_SIZE) : 1;
					const selectedRowKeys: any = getTableSelection(getState(), options.tableId);
					let { data = [], columns } = response;
					const backendCount = data?.[0]?.TotalRecordCount ?? 0;
					const resolvedRowCount = backendCount < data.length ? data.length : backendCount;

					if (pageNumber === 1) {
						if (tableId === 115) {
							totalRecordCount = resolvedRowCount;
						} else {
							totalRecordCount = data?.[0]?.TotalRecordCount ?? 0;
						}
					}

					params.success({
						rowData: data.length
							? _.map(data, (d) => {
									_.keys(d).forEach((k) => {
										if (d[k] === null) {
											d[k] = 'null';
										}
									});
									return d;
								})
							: [],
						rowCount: tableId === 115 ? resolvedRowCount : (totalRecordCount ?? 0),
					});

					const hasGridData = getGridData(getState(), options.tableId)?.length;
					if ((pageNumber > 1 || hasGridData) && selectedRowKeys?.isSelectAll) {
						gridRef?.current?.api?.forEachNode(function (node) {
							node.setSelected(true);
						});
					}
					if (pageNumber === 1 && selectedRowKeys?.selectedRowKeys?.length && !selectedRowKeys?.isSelectAll) {
						const gridMetaData = getGridMetadata(getState(), options.tableId);
						gridRef?.current?.api?.forEachNode(function (node) {
							if (selectedRowKeys?.selectedRowKeys.includes(node?.data?.[gridMetaData?.selectRowKey])) {
								node.setSelected(true);
							}
						});
					}

					const aggregationColumns = params.request.valueCols.map((v) => v.field);
					const rootNode = { groupId: '', headerName: '', children: [] };

					if (columns && Object.keys(columns)?.length) {
						if (tableId === 115) {
							return;
						} else {
							buildTree(columns, Object.keys(columns), rootNode, 0, aggregationColumns, options.tableId);
							setSecondaryColsIntoGrid(rootNode.children);
							return;
						}
					}

					if (!totalRecordCount) {
						gridRef?.current?.api.showNoRowsOverlay();
					}
				})
				.catch((e) => {
					console.log(e);
					params.fail();
				});
		},
	};
}

export const createServer = (tableId) => {
	return {
		getData: (request, options, gridRef) => {
			const dispatch = getDispatch();
			let sortField = getColumnInfoByIndentifier(
				getState(),
				tableId,
				request?.sortModel?.[0]?.colId
			)?.AppColumnQueryName;

			let sortModel =
				request?.groupKeys?.length !== request?.rowGroupCols?.length
					? _.filter(
							request.sortModel,
							(s) =>
								request?.rowGroupCols?.find((c) => c.id === s.colId) ||
								request?.valueCols?.find((c) => c.id === s.colId)
						)
					: request.sortModel;
			const pivotColumns = _.map(request.pivotCols, (col) => ({
				AliasName: col.field,
				QueryName: getColumnInfoByIndentifier(getState(), tableId, col.id)?.AppColumnQueryName,
			}));
			let sortDirection = sortModel?.[0]?.sort === 'asc' ? 'ASC' : 'DESC';
			let multiSortColumns = _.map(sortModel, (f) => ({
				sortField:
					request.pivotMode || options.isViewImplementationOverride
						? getColumnInfoByIndentifier(getState(), tableId, f.colId)?.AppColumnName
						: getColumnInfoByIndentifier(getState(), tableId, f.colId)?.AppColumnQueryName,
				sortDirection: f.sort,
			}));
			// Calculate groupAggregation: needed when NOT at leaf level (groupKeys.length < rowGroupCols.length)
			// At leaf level (groupKeys.length === rowGroupCols.length), we show individual rows, but
			// we should preserve existing aggregation in filter state to keep parent aggregated rows visible
			const isAtLeafLevel = request?.groupKeys?.length === request?.rowGroupCols?.length;
			const currentFilters = getGridFilters(getState(), tableId);
			const existingGroupAggregation = currentFilters?.groupAggregation || [];

			let groupAggregation;
			if (request.rowGroupCols.length && !isAtLeafLevel) {
				// Not at leaf level - calculate aggregation from valueCols
				groupAggregation = request.valueCols
					.filter((v) =>
						request.pivotMode ? !Boolean(request?.rowGroupCols?.find((g) => g.id === v.id)) : true
					)
					.map((v) => ({
						QueryName: `${v.aggFunc}(${
							request.pivotMode || options.isViewImplementationOverride
								? getColumnInfoByIndentifier(getState(), tableId, v.field).AppColumnName
								: getColumnInfoByIndentifier(getState(), tableId, v.field).AppColumnQueryName
						})`,
						AliasName: v.field,
					}));
			} else if (isAtLeafLevel && existingGroupAggregation.length > 0) {
				// At leaf level but preserve existing aggregation to keep parent aggregated rows visible
				groupAggregation = existingGroupAggregation;
			} else {
				// No aggregation needed
				groupAggregation = [];
			}
			if (tableId === 115) {
				multiSortColumns = request.sortModel.map((f) => ({
					sortField: f.colId.split('|')[1] || f.colId, // fallback if no '|'
					sortDirection: f.sort,
				}));
			} else if (groupAggregation.length) {
				multiSortColumns = _.map(sortModel, (f) => ({
					sortField: groupAggregation.find(
						(g) => g.AliasName === getColumnInfoByIndentifier(getState(), tableId, f.colId)?.AppColumnName
					)
						? groupAggregation.find(
								(g) =>
									g.AliasName ===
									getColumnInfoByIndentifier(getState(), tableId, f.colId)?.AppColumnName
							).QueryName
						: getColumnInfoByIndentifier(getState(), tableId, f.colId)?.AppColumnName,
					sortDirection: f.sort,
				}));
			}
			dispatch(
				dynamicGridSagaActions.updateGridFilters(
					tableId,
					{
						rowGroupCols: _.map(request.rowGroupCols, (col) => {
							let selected = getColumnInfoByIndentifier(getState(), tableId, col.field);
							return {
								AliasName: selected.AppColumnName,
								QueryName:
									request.pivotMode || options.isViewImplementationOverride
										? selected.AppColumnName
										: selected.AppColumnQueryName,
							};
						}),
						groupKeys: request.groupKeys,
						pivotColumns: request.pivotMode ? pivotColumns : [],
						isPivotMode: request.pivotMode,
						isMultiSort: multiSortColumns?.length,
						multiSortColumns,
						groupAggregation,
					},
					true,
					Math.ceil(request.endRow / PAGE_SIZE),
					options.isStorageEnabled
				)
			);
			// in this simplified fake server all rows are contained in an array
			return fetchGridData({
				payload: {
					tableId,
					isAdvancedSearch: true,
					sortDirection,
					sortField,
					multiSortColumns,
					isMultiSort: multiSortColumns?.length,
					pageNumber: Math.ceil(request.endRow / PAGE_SIZE),
					isAppend: Math.ceil(request.endRow / PAGE_SIZE) > 1,
					itemsPerPage: PAGE_SIZE,
					isView: request.pivotMode || options.isViewImplementationOverride,
					isPivot: request.pivotMode,
					...options,
				},
			});
		},
	};
};

export const getAllowedAggFuncs = (colType) => {
	switch (colType) {
		case 'number':
		case 'qty':
			return ['sum', 'min', 'max', 'count', 'avg'];
		case 'price':
			return ['min', 'max', 'count'];
		case 'date':
			return ['min', 'max'];
		case 'percentage':
			return ['sum', 'min', 'max', 'avg'];
		case 'percentagefull':
			return ['sum', 'min', 'max', 'avg'];
		case 'text':
		default:
			return ['min', 'max', 'count'];
	}
};

export const getToolPanelConfig = ({ rowGroupPanelShow, rowPivotPanelShow, tableId }) => {
	const toolPanels = [
		{
			id: 'columns',
			labelDefault: 'Columns',
			labelKey: 'columns',
			iconKey: 'columns',
			toolPanel: 'agColumnsToolPanel',
			toolPanelParams: {
				suppressRowGroups: !rowGroupPanelShow,
				suppressValues: !rowPivotPanelShow,
				suppressPivots: !rowPivotPanelShow, // used in AG Grid 32 instead of `suppressPivotMode`
			},
		},
		{
			id: 'filters',
			labelDefault: 'Filters',
			labelKey: 'filters',
			iconKey: 'filter',
			toolPanel: 'agFiltersToolPanel',
		},
		{
			id: 'bookmarksPanel',
			labelDefault: 'Bookmarks',
			labelKey: 'bookmarksPanel',
			iconKey: 'menu',
			toolPanel: Bookmarks, // Make sure this is registered if it's custom
			toolPanelParams: {
				tableId,
			},
		},
	];

	return {
		toolPanels,
	};
};

export const getActionColumn = ({ tableId, gridRef, customActions, isGroupingEnabled, gridConfig }) => ({
	field: '',
	lockPosition: 'right',
	pinned: 'right',
	metaData: { tableId },
	cellRenderer: (props) => (
		<GridActionHandler {...props} gridConfig={gridConfig} customActions={customActions}></GridActionHandler>
	),
	cellRendererParams: {
		prop1: 'example',
	},
	suppressMenu: true,
	suppressColumnsToolPanel: true,
	fixed: true,
	hide: false,
	width: customActions ? customActions.width || 200 : 50,
});
